import { ADMIN_REPORT_STATUSES, type AdminReportStatus } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the reports list (AUT-127, re-defaulted by
 * AUT-265). Mirrors the backend `ListAdminReportsQuerySchema` — `status`
 * is a multi-select over the full 5-state `ADMIN_REPORT_STATUSES`,
 * `inspectorId` is an exact-match UUID (the admin drills into one
 * inspector's reports), `dateFrom` / `dateTo` are ISO date strings
 * binding the backend's `submittedAt` filter (architect 🟡 #3 — NOT
 * createdAt), and `q` is a free-text search ≥3 chars over `reportNumber`
 * only.
 *
 * **The default is now every status, not `['submitted']`.** The old
 * default described a moderation queue: "these are waiting for you".
 * Post-AUT-265 `submitted` means "the inspector called it final and the
 * customer has not accepted yet" — waiting on the CUSTOMER, actionable
 * by nobody in this panel. Landing on that list makes a passive backlog
 * look like a to-do list. Reports are a reference an adjudicator looks
 * things up in, so the honest default is "all of them, newest first".
 *
 * Naming every value explicitly is deliberate: the backend applies its
 * OWN `['submitted']` default when `status` is absent, so an unfiltered
 * view cannot be expressed by omitting the param.
 *
 * Every key has a `.catch` default so a malformed / absent query param
 * degrades safely instead of throwing — `useUrlFilters` parses the raw
 * query string straight through.
 */

/** Min free-text search length (mirrors backend MIN_SEARCH_LENGTH). */
export const MIN_SEARCH_LENGTH = 3;

/** Default LIST filter — every status (see the module docblock). */
export const DEFAULT_REPORT_STATUSES: readonly AdminReportStatus[] = ADMIN_REPORT_STATUSES;

/**
 * `status=submitted,approved` → string[] of valid statuses. When the raw
 * param is absent, defaults to the full set (unfiltered view). When
 * present-but-empty (`status=`), degrades to `[]` — the SPA chose to
 * clear it explicitly, and the api client then omits the param, letting
 * the backend's own `['submitted']` default apply.
 */
const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) => {
    if (raw === undefined) return [...DEFAULT_REPORT_STATUSES];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is AdminReportStatus =>
        (ADMIN_REPORT_STATUSES as readonly string[]).includes(s),
      );
  });

const qSchema = z.string().optional().catch(undefined);
const inspectorIdSchema = z.string().optional().catch(undefined);
const dateSchema = z.string().optional().catch(undefined);

export const reportFiltersSchema = z.object({
  status: statusSchema,
  inspectorId: inspectorIdSchema,
  dateFrom: dateSchema,
  dateTo: dateSchema,
  q: qSchema,
});

export type ReportFilters = z.infer<typeof reportFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = ADMIN_REPORT_STATUSES.map((value) => ({
  value,
  labelKey: `reports.status.${value}`,
}));

/** True when the status selection is a strict, non-empty subset. */
const isNarrowedStatus = (status: AdminReportStatus[] | undefined): boolean => {
  const count = status?.length ?? 0;
  return count > 0 && count < ADMIN_REPORT_STATUSES.length;
};

/**
 * True when any managed filter is active — drives the "clear filters"
 * link. A status selection only counts when it NARROWS: the default is
 * every status, so "all selected" renders the same view as no filter,
 * and offering to clear it would be a no-op button.
 */
export const hasActiveFilters = (filters: ReportFilters): boolean =>
  isNarrowedStatus(filters.status) ||
  Boolean(filters.inspectorId && filters.inspectorId.length > 0) ||
  Boolean(filters.dateFrom && filters.dateFrom.length > 0) ||
  Boolean(filters.dateTo && filters.dateTo.length > 0) ||
  Boolean(filters.q && filters.q.length > 0);
